import { ServiceLayer } from "./ServiceLayer.js";
import { colorScaleToWmsParams, copyColorScale } from "../../ui/color/Utils.js";
import { EventObject } from "../../events/EventObject.js";

export { WMSLayer }

/**
 * WMS Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @memberof vef.map.layer
 */
class WMSLayer extends ServiceLayer {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // init default values and define getters and setters
        this.addDefaults_({
            minX: -180,
            maxX: 180,
            minY: -90,
            maxY: 90,
            getFeatureInfoName: null,
            availableStyles: {},
            // overwrite existing defaults
            opacitySettings: true,
            styleSettings: true,
            icon: "vef vef-raster"
        });

        this.setAll_("type", "wms");

        // internal properties
        this.zIndex_ = 0;
        this.queryParams_ = {};
        this.filterParams_ = {};
        this.changedParams_ = [];
        this.featureInfoHelper_ = new FeatureInfoHelper();
    }

    /**
     * create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type) {
        let layer = null;
        switch (type) {
            case "leaflet":
                layer = L.tileLayer.wms(this.serviceUrl, {
                    layers: this.getRequestName(),
                    version: this.serviceVersion,
                    attribution: this.attribution,
                    format: 'image/png',
                    transparent: true,
                    maxZoom: 30
                });

                break;
            case "three-globe":
                layer = new EventObject({
                    "loading": [],
                    "load": [],
                    "redraw_wms": []
                });

                break;
        }

        if (layer) {
            layer.on('loading', () => this.fire("loading"));
            layer.on('load', () => this.fire("load"));
            this.mapLayers_[type] = layer;
        }

        return layer;
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        this.activeMap_ = type;

        // call setters to apply options to the active mapLayer
        if (Number.isFinite(this.opacity)) this.setOpacity(this.opacity);
        if (Number.isFinite(this.zIndex_)) this.setZIndex(this.zIndex_);
        this.setProjection(this.crs_);

        // set the color scale or a style if defined
        if (this.colorScale) {
            this.setColorScale(this.colorScale, true);
        } else if (this.staticQueryParameters.styles) {
            this.setStyle(this.staticQueryParameters.styles, true);
        } else if ((this.serviceSoftware == "rasdaman") && !this.getStyle() && (Object.keys(this.availableStyles).length > 0)) {
            // apply first style for rasdaman, because the default style is not returned automatically be the service
            this.setStyle(Object.keys(this.availableStyles)[0], true);
        } else {
            this.setStyle("", true);
        }

        // apply filter, static filter and static query parameters
        this.applyFilter(true);

        // reload was intentionally prevented in the setters to call it in the end
        this.reload();
    }

    /**
     * Returns the Legend Graphic for this layer.
     * May depend on current color scale.
     * 
     * @returns html <img>-tag (null or undefined if there is no legend graphic)
     */
    getLegendGraphic() {
        let url = this.legendUrl || `${this.serviceUrl}?REQUEST=GetLegendGraphic`;
        const urlLowerCase = url.toLowerCase();

        const parts = url.split("?");
        if (urlLowerCase.includes("request=getlegendgraphic") && (parts.length == 2)) {
            const params = parts[1].split("&");
            const style = this.getStyle();

            // set current style if the predefined style is empty.
            // cannot be done with "addParam" because "style=" already exists as an empty property
            for (let i = 0; i < params.length; ++i) {
                if (params[i].toLowerCase() == "style=") {
                    params[i] += encodeURIComponent(style);
                }
            }

            // only add param if it does not exist
            const addParam = (key, value) => {
                if (value && !urlLowerCase.includes(key.toLowerCase() + "=")) {
                    params.push(key + "=" + encodeURIComponent(value));
                }
            };

            addParam("SERVICE", "WMS");
            addParam("LAYER", this.getRequestName());
            addParam("STYLE", style);
            addParam("ENV", this.queryParams_.env);
            addParam("TRANSPARENT", "true");
            addParam("FORMAT", "image/png");
            addParam("VERSION", this.serviceVersion);
            addParam("LEGEND_OPTIONS", "forceLabels:on;fontSize:16;fontAntiAliasing:true;fontName:PT Sans");

            url = parts[0] + ((params.length) ? ("?" + params.join("&")) : "");
        }

        const image = document.createElement("img");
        image.src = url;

        return image;
    }

    /**
     * Switches the projection for the visualization
     * of the leaflet layer. CRS hast to be included in availableCRS
     * 
     * @param {string} crs target crs code
     * @returns {boolean} returns true if successful
     */
    setProjection(crs) {
        const code = crs.replace(":", "");
        if (this.availableCrs.includes(crs) && (code in L.CRS)) {
            this.crs_ = crs;
            if (this.activeMap_ in this.mapLayers_) {
                switch (this.activeMap_) {
                    case "leaflet":
                        this.mapLayers_.leaflet.options.crs = L.CRS[code];
                        this.mapLayers_.leaflet.options.layers = this.getRequestName();
                        this.mapLayers_.leaflet.wmsParams.layers = this.getRequestName();
                }
            }
            return true;
        }
        return false;
    }

    /**
     * Set the style of the layer. Does not check if a style exists in availableStyles (non-listed styles are allowed)
     * 
     * @param {string} styleName style name
     * @param {boolean} noReload prevents reload if true
     */
    setStyle(styleName, noReload) {
        // remove "env" from colorscale settings
        if (typeof this.staticQueryParameters.env == "string") delete this.staticQueryParameters.env;
        if (typeof this.queryParams_.env == "string") delete this.queryParams_.env;

        if (styleName) {
            this.staticQueryParameters.styles = styleName;
            this.queryParams_.styles = styleName;
        } else {
            if ("styles" in this.staticQueryParameters) delete this.staticQueryParameters.styles;
            if ("styles" in this.queryParams_) delete this.queryParams_.styles;
        }
        this.colorScale = null;

        if (!noReload) this.reload();
    }

    /**
     * Get the active style of the layer.
     */
    getStyle() {
        return ("styles" in this.staticQueryParameters) ? this.staticQueryParameters.styles : "";
    }

    /**
     * Set the colorscale for the layer
     * 
     * @param colorScale 
     * @param {boolean} noReload prevents reload if true
     */
    setColorScale(colorScale, noReload) {
        this.colorScale = copyColorScale(colorScale);

        const params = colorScaleToWmsParams(colorScale);
        Object.assign(this.staticQueryParameters, params);
        Object.assign(this.queryParams_, params);

        if (!noReload) this.reload();
    }

    /**
     * Get the active colorscale for the layer
     */
    getColorScale() {
        return (this.colorScale) ? copyColorScale(this.colorScale) : null;
    }

    getQueryParams_() {
        return Object.assign({}, this.queryParams_, this.filterParams_);
    }

    /**
     * Reload the Layer on the map
     */
    reload() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        const params = this.getQueryParams_();
        // apply the current query parameters to the layer
        switch (this.activeMap_) {
            case "leaflet":
                this.changedParams_.forEach(param => {
                    if ((param in this.mapLayers_.leaflet.wmsParams) && !(param in params)) {
                        delete this.mapLayers_.leaflet.wmsParams[param];
                    }
                });
                for (let key in params) {
                    if (!this.changedParams_.includes(key)) this.changedParams_.push(key);
                    if (params[key] !== this.mapLayers_.leaflet.wmsParams[key]) {
                        if (params[key]) {
                            this.mapLayers_.leaflet.wmsParams[key] = params[key];
                        } else if (this.mapLayers_.leaflet.wmsParams[key]) {
                            delete this.mapLayers_.leaflet.wmsParams[key];
                        }
                    }
                }
                // FIXME: The layers redraw function results in misaligned tiles when the map was previously zoomed out completely.
                //         Removing and adding the Layer again is a workaround for this issue.
                // this.mapLayers_.leaflet.redraw();
                if (this.mapLayers_.leaflet._map) {
                    const map = this.mapLayers_.leaflet._map;
                    this.mapLayers_.leaflet.remove();
                    this.mapLayers_.leaflet.addTo(map);
                }
                break;
            case "three-globe":
                this.mapLayers_["three-globe"].fire("redraw_wms", this);
                break;
        }
    }

    /**
     * Apply a filter on the layer. Uses WMS filter string
     * 
     * @param {boolean} noReload prevents reload if true
     */
    applyFilter(noReload) {
        const params = (this.filter) ? this.createFilterParams_(this.filter) : {};
        const changed = !_.isEqual(params, this.filterParams_);
        this.filterParams_ = params;
        if (!noReload && changed) this.reload();
    }

    /**
     * Get feature info for a specific location.
     * e.g. for displaying it in a map-popup
     * 
     * @param {object} options getFeatureInfo params from map 
     * @returns {Promise} feature info promise
     */
    getFeatureInfo(options) {
        const helper = this.featureInfoHelper_;
        return (this.serviceSoftware.toLowerCase() in helper)
            ? helper[this.serviceSoftware.toLowerCase()](this, options)
            : helper.geoserver(this, options)
            ;
    }

    /**
     * Set the transparency between 0 and 1
     * @override
     * @param {number} opacity style name
     */
    setOpacity(opacity) {
        this.opacity = opacity;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.setOpacity(opacity);
                break;
            case "three-globe":
                this.mapLayers_["three-globe"].fire("redraw_wms", this);
                break;
        }
    }

    /**
     * Set the z-index of a layer
     * @param {number} index 
     */
    setZIndex(index) {
        this.zIndex_ = index;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.setZIndex(index);
                break;
            case "three-globe":
                this.mapLayers_["three-globe"].fire("redraw_wms", this);
                break;
        }
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        return {
            min: {
                x: (this.minX < this.maxX) ? this.minX : this.maxX,
                y: (this.minY < this.maxY) ? this.minY : this.maxY

            },
            max: {
                x: (this.minX > this.maxX) ? this.minX : this.maxX,
                y: (this.minY > this.maxY) ? this.minY : this.maxY
            },
            crs: "CRS:84"
        }
    }

}


class FeatureInfoHelper {

    /**
     * Requests the "getFeatureInfo" method defined by url and responses an
     * array of objects containing the original data and popup styled content.
     *
     * @private
     * @param {string} url
     */
    requestFeatureInfo_(baseUrl, params, upperCase = true) {
        if (this.request_) this.request_.abort();
        this.request_ = new XMLHttpRequest();

        baseUrl = baseUrl + "?";

        const url = baseUrl + L.Util.getParamString(params, baseUrl, upperCase);
        console.log("The URL queried is: ", url);

        return new Promise(resolve => {
            const xhttp = this.request_;
            xhttp.onreadystatechange = () => {
                if (xhttp.readyState == 4) {
                    let response = null;
                    if (xhttp.status == 200) {
                        const type = xhttp.getResponseHeader('content-type').toLowerCase();
                        try {
                            if (type.includes('json')) {
                                response = JSON.parse(xhttp.responseText);
                            } else if (type.includes('xml')) {
                                response = new DOMParser().parseFromString(xhttp.responseText, "text/xml")
                            } else {
                                response = xhttp.responseText;
                            }
                        } catch (e) {
                            console.warn("Could not parse " + type + " response!", e);
                        }
                    } else {
                        console.warn("HTTP status code is " + xhttp.status);
                    }
                    this.request_ = null;
                    resolve(response);
                }
            };

            xhttp.open("GET", url, true);
            xhttp.send();
        });
    }

    createGetFeatureInfoParams_(layer, options, version) {
        version = version || layer.serviceVersion;

        const layers = layer.getFeatureInfoName || layer.getRequestName();
        const min = options.min;
        const max = options.max;

        const params = {
            service: "WMS",
            request: "GetFeatureInfo",
            feature_count: 50,
            buffer: 25,
            layers: layers,
            query_layers: layers,
            styles: layer.getStyle(),
            width: options.container.width,
            height: options.container.height,
            version: version
        };

        // version specific adjustments
        params.bbox = ((version === "1.3.0") && (options.crs == "EPSG:4326"))
            ? [min.y, min.x, max.y, max.x].join(",")
            : [min.x, min.y, max.x, max.y].join(",")
            ;
        params[version === "1.3.0" ? "I" : "X"] = options.container.x;
        params[version === "1.3.0" ? "J" : "Y"] = options.container.y;
        params[version === "1.3.0" ? "CRS" : "SRS"] = options.crs;

        const layerParams = layer.getQueryParams_();
        // Add additional Params
        ["filter", "cql_filter", "time", "env", "query", "layerDefs"].forEach(param => {
            if (param in layerParams) {
                params[param] = layerParams[param];
            }
        });

        return params;
    }

    geoserver(layer, options) {
        return new Promise(resolve => {
            const params = this.createGetFeatureInfoParams_(layer, options);
            params.info_format = "application/json";

            // sort results by the correct time column
            let timeColumn = null;
            for (let i = 0; i < layer.timeColumns_.length; ++i) {
                const column = layer.timeColumns_[i];
                if ((column in layer.dimensions) && (layer.dimensions[column].type == "attribute_field")) {
                    timeColumn = layer.dimensions[column].name || column;
                } else if (column in layer.attributeFields) {
                    timeColumn = column;
                }
                if (timeColumn) {
                    params.sortBy = timeColumn + " A";
                    break;
                }
            }

            this.requestFeatureInfo_(layer.serviceUrl, params)
                .then(response => resolve({ layer: layer, data: response }));
        });
    }

    thredds(layer, options) {
        return new Promise(resolve => {
            const params = this.createGetFeatureInfoParams_(layer, options);
            params.info_format = "text/xml";

            this.requestFeatureInfo_(layer.serviceUrl, params)
                .then(response => {
                    if (!(response instanceof XMLDocument)) {
                        return resolve({ layer: layer, data: response });
                    }
                    const featureCollection = {
                        type: "FeatureCollection",
                        features: []
                    };
                    const lng = parseFloat(response.querySelector("longitude").textContent.trim());
                    const lat = parseFloat(response.querySelector("latitude").textContent.trim());
                    response.querySelectorAll("FeatureInfoResponse FeatureInfo").forEach(xmlFeature => {
                        const feature = {
                            type: "Feature",
                            geometry: {
                                type: "Point",
                                coordinates: [lng, lat]
                            },
                            properties: {}
                        };
                        xmlFeature.childNodes.forEach(child => {
                            if (child.nodeType === 1) {
                                feature.properties[child.tagName.trim()] = child.textContent.trim()
                            }
                        });
                        featureCollection.features.push(feature)
                    });
                    resolve({ layer: layer, data: featureCollection });
                });
        });
    }

    esri(layer, options) {
        return new Promise(resolve => {
            const params = this.createGetFeatureInfoParams_(layer, options, "1.1.1");
            params.info_format = "text/xml";

            this.requestFeatureInfo_(layer.serviceUrl, params)
                .then(response => {
                    if (!(response instanceof XMLDocument)) {
                        return resolve({ layer: layer, data: response });
                    }
                    const featureCollection = {
                        type: "FeatureCollection",
                        features: []
                    };
                    response.querySelectorAll("FeatureInfoResponse FIELDS").forEach(xmlFeature => {
                        const feature = {
                            type: "Feature",
                            properties: {}
                        };
                        for (let i = 0; i < xmlFeature.attributes.length; ++i) {
                            const attribute = xmlFeature.attributes[i];
                            feature.properties[attribute.name] = attribute.value;
                        }
                        featureCollection.features.push(feature)
                    });
                    resolve({ layer: layer, data: featureCollection });
                });
        });
    }

    esri_identify(layer, options) {
        return new Promise(resolve => {
            const layers = layer.getFeatureInfoName || layer.getRequestName();
            const min = options.min;
            const max = options.max;

            const params = {
                geometryPrecision: 1,
                returnZ: "false",
                returnM: "false",
                returnGeometry: "false",
                tolerance: 25,
                f: "json",
                geometryType: "esriGeometryPoint",
                mapExtent: [min.x, min.y, max.x, max.y].join(","),
                geometry: [options.click.x, options.click.y].join(","),
                imageDisplay: options.container.width + "," + options.container.height + ",96",
                layers: "all:" + layers,
                sr: options.crs
            };
            // only add filter and time dimension if defined
            const currentParams = layer.getQueryParams_();
            if (currentParams.layerDefs) params.layerDefs = currentParams.layerDefs;
            if (currentParams.time) params.time = currentParams.time;

            this.requestFeatureInfo_(layer.serviceUrl + "/identify", params, false)
                .then(response => resolve({ layer: layer, data: response }));
        });
    }
}